<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12">
        <b-card>
          <div slot="header">
            <i class="fa fa-history mr-2"></i> Audit Log
            <div class="card-header-actions">
              <b-button variant="outline-primary" size="sm" @click="refresh" :disabled="loading">
                <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> Refresh
              </b-button>
            </div>
          </div>

          <!-- Filters -->
          <b-row class="mb-3">
            <b-col md="3" sm="6" class="mb-2">
              <label class="small text-muted d-block mb-1">Entity Type</label>
              <b-form-select v-model="filters.entityType" :options="entityTypeOptions" size="sm" @change="onFilterChange" />
            </b-col>
            <b-col md="3" sm="6" class="mb-2">
              <label class="small text-muted d-block mb-1">Action</label>
              <b-form-select v-model="filters.action" :options="actionOptions" size="sm" @change="onFilterChange" />
            </b-col>
            <b-col md="4" sm="8" class="mb-2">
              <label class="small text-muted d-block mb-1">Date Range</label>
              <b-input-group size="sm">
                <b-form-input type="date" v-model="filters.dateFrom" @change="onFilterChange" />
                <b-input-group-text class="px-1"><small>to</small></b-input-group-text>
                <b-form-input type="date" v-model="filters.dateTo" @change="onFilterChange" />
              </b-input-group>
            </b-col>
            <b-col md="2" sm="4" class="mb-2 d-flex align-items-end">
              <b-button variant="outline-secondary" size="sm" @click="clearFilters" class="w-100">
                <i class="fa fa-times mr-1"></i> Clear
              </b-button>
            </b-col>
          </b-row>

          <!-- Loading -->
          <div v-if="loading" class="text-center py-5">
            <b-spinner variant="primary" style="width:2rem;height:2rem;"></b-spinner>
            <p class="mt-2 text-muted">Loading audit log&hellip;</p>
          </div>

          <!-- Error -->
          <b-alert v-else-if="error" show variant="danger">
            <i class="fa fa-exclamation-triangle mr-1"></i> {{ error.message || 'Failed to load audit log.' }}
          </b-alert>

          <!-- Empty -->
          <div v-else-if="!auditLogs || auditLogs.length === 0" class="text-center py-5 text-muted">
            <i class="fa fa-history fa-3x mb-3"></i>
            <h5>No audit log entries found</h5>
            <p class="small">Try adjusting your filters.</p>
          </div>

          <!-- Table -->
          <div v-else>
            <b-table
              :items="auditLogs"
              :fields="fields"
              striped
              hover
              responsive
              show-empty
            >
              <template #cell(createdAt)="data">
                <span :title="data.value">{{ formatDate(data.value) }}</span>
              </template>

              <template #cell(action)="data">
                <b-badge :variant="actionVariant(data.value)" class="text-monospace" style="font-size:0.75rem;">
                  {{ data.value }}
                </b-badge>
              </template>

              <template #cell(entityType)="data">
                <b-badge variant="secondary">{{ data.value }}</b-badge>
              </template>

              <template #cell(entityName)="data">
                <span class="text-truncate d-inline-block" style="max-width:160px;" :title="data.value || data.item.entityId">
                  {{ data.value || data.item.entityId }}
                </span>
              </template>

              <template #cell(userEmail)="data">
                <span class="text-muted small">{{ data.value || '—' }}</span>
              </template>

              <template #cell(changes)="data">
                <b-button
                  v-if="data.value && Object.keys(data.value).length"
                  size="sm"
                  variant="outline-primary"
                  @click="openDiff(data.item)"
                >
                  <i class="fa fa-code mr-1"></i> {{ Object.keys(data.value).length }} field{{ Object.keys(data.value).length === 1 ? '' : 's' }}
                </b-button>
                <span v-else class="text-muted small">—</span>
              </template>
            </b-table>

            <!-- Pagination -->
            <div class="d-flex justify-content-between align-items-center mt-2">
              <small class="text-muted">
                Showing {{ auditLogs.length }} of {{ totalCount }} entries
              </small>
              <div>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="mr-1"
                  :disabled="currentPage === 0"
                  @click="prevPage"
                >
                  <i class="fa fa-chevron-left"></i> Prev
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="auditLogs.length < pageSize"
                  @click="nextPage"
                >
                  Next <i class="fa fa-chevron-right"></i>
                </b-button>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Diff Modal -->
    <b-modal
      v-model="diffModal.show"
      :title="diffModal.title"
      size="lg"
      hide-footer
      @hidden="diffModal.entry = null"
    >
      <div v-if="diffModal.entry">
        <!-- Meta row -->
        <b-row class="mb-3">
          <b-col sm="4">
            <small class="text-muted text-uppercase d-block" style="font-size:0.68rem;letter-spacing:0.04em;">Performed By</small>
            <span class="font-weight-bold small">{{ diffModal.entry.userEmail || '—' }}</span>
          </b-col>
          <b-col sm="4">
            <small class="text-muted text-uppercase d-block" style="font-size:0.68rem;letter-spacing:0.04em;">Entity</small>
            <span class="small">
              <b-badge variant="secondary" class="mr-1">{{ diffModal.entry.entityType }}</b-badge>
              {{ diffModal.entry.entityName || diffModal.entry.entityId }}
            </span>
          </b-col>
          <b-col sm="4">
            <small class="text-muted text-uppercase d-block" style="font-size:0.68rem;letter-spacing:0.04em;">Time</small>
            <span class="small">{{ formatDateFull(diffModal.entry.createdAt) }}</span>
          </b-col>
        </b-row>

        <hr class="mt-0 mb-3">

        <!-- Changes table -->
        <div v-if="diffModal.entry.changes && Object.keys(diffModal.entry.changes).length">
          <p class="small text-muted mb-2">
            <i class="fa fa-info-circle mr-1"></i>
            {{ isCreateAction(diffModal.entry.action) ? 'Record created with the following values:' : 'The following fields were updated:' }}
          </p>
          <b-table-simple small bordered responsive>
            <b-thead head-variant="light">
              <b-tr>
                <b-th style="width:35%">Field</b-th>
                <b-th>{{ isCreateAction(diffModal.entry.action) ? 'Value' : 'New Value' }}</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="(val, key) in diffModal.entry.changes" :key="key">
                <b-td class="text-monospace" style="font-size:0.82rem;">{{ key }}</b-td>
                <b-td>
                  <span v-if="val === null || val === undefined" class="text-muted font-italic small">null</span>
                  <span v-else-if="typeof val === 'boolean'">
                    <b-badge :variant="val ? 'success' : 'secondary'">{{ val ? 'true' : 'false' }}</b-badge>
                  </span>
                  <span v-else-if="typeof val === 'object'" class="text-monospace small" style="white-space:pre-wrap;">{{ JSON.stringify(val, null, 2) }}</span>
                  <span v-else class="font-weight-bold small">{{ val }}</span>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </div>

        <div v-else class="text-muted small text-center py-3">
          No field changes recorded for this action.
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { AUDIT_LOGS_QUERY } from '@/constants/graphql'

const PAGE_SIZE = 50

const ACTION_VARIANTS = {
  createDevice: 'success',
  updateDevice: 'primary',
  updateUser: 'primary',
  updateUserRole: 'warning',
  adminResetUserPassword: 'warning',
  addUserToTeam: 'success',
  removeUserFromTeam: 'danger',
  addDeviceToTeam: 'success',
  updateTeamDevice: 'primary',
  removeDeviceFromTeam: 'danger'
}

const CREATE_ACTIONS = ['createDevice', 'addUserToTeam', 'addDeviceToTeam']

const ENTITY_TYPES = ['Device', 'User', 'TeamMember', 'TeamDevice']
const ACTIONS = Object.keys(ACTION_VARIANTS)

export default {
  name: 'AuditLog',
  data () {
    return {
      loading: false,
      error: null,
      auditLogs: [],
      totalCount: 0,
      currentPage: 0,
      pageSize: PAGE_SIZE,
      filters: {
        entityType: null,
        action: null,
        dateFrom: '',
        dateTo: ''
      },
      diffModal: {
        show: false,
        title: '',
        entry: null
      },
      fields: [
        { key: 'createdAt', label: 'Time', sortable: false },
        { key: 'action', label: 'Action', sortable: false },
        { key: 'entityType', label: 'Type', sortable: false, thClass: 'd-none d-sm-table-cell', tdClass: 'd-none d-sm-table-cell' },
        { key: 'entityName', label: 'Entity', sortable: false },
        { key: 'userEmail', label: 'Performed By', sortable: false, thClass: 'd-none d-md-table-cell', tdClass: 'd-none d-md-table-cell' },
        { key: 'changes', label: 'Changes', sortable: false, class: 'text-right' }
      ],
      entityTypeOptions: [
        { value: null, text: 'All Types' },
        ...ENTITY_TYPES.map(function (t) { return { value: t, text: t } })
      ],
      actionOptions: [
        { value: null, text: 'All Actions' },
        ...ACTIONS.map(function (a) { return { value: a, text: a } })
      ]
    }
  },
  mounted () {
    this.fetchLogs()
  },
  methods: {
    buildWhere () {
      var where = {}
      if (this.filters.entityType) where.entityType = this.filters.entityType
      if (this.filters.action) where.action = this.filters.action
      if (this.filters.dateFrom) where.createdAt_gte = new Date(this.filters.dateFrom).toISOString()
      if (this.filters.dateTo) {
        // Include the full dateTo day
        var end = new Date(this.filters.dateTo)
        end.setHours(23, 59, 59, 999)
        where.createdAt_lte = end.toISOString()
      }
      return Object.keys(where).length ? where : undefined
    },

    async fetchLogs () {
      this.loading = true
      this.error = null
      try {
        var result = await this.$apollo.query({
          query: AUDIT_LOGS_QUERY,
          variables: {
            skip: this.currentPage * this.pageSize,
            limit: this.pageSize,
            orderBy: { createdAt: 'desc' },
            where: this.buildWhere()
          },
          fetchPolicy: 'network-only'
        })
        this.auditLogs = result.data.auditLogs || []
        // No total count from API — infer from result size + offset
        this.totalCount = this.currentPage * this.pageSize + this.auditLogs.length +
          (this.auditLogs.length === this.pageSize ? 1 : 0)
      } catch (e) {
        this.error = e
      } finally {
        this.loading = false
      }
    },

    refresh () {
      this.currentPage = 0
      this.fetchLogs()
    },

    onFilterChange () {
      this.currentPage = 0
      this.fetchLogs()
    },

    clearFilters () {
      this.filters = { entityType: null, action: null, dateFrom: '', dateTo: '' }
      this.currentPage = 0
      this.fetchLogs()
    },

    nextPage () {
      this.currentPage++
      this.fetchLogs()
    },

    prevPage () {
      if (this.currentPage > 0) {
        this.currentPage--
        this.fetchLogs()
      }
    },

    openDiff (entry) {
      this.diffModal.entry = entry
      this.diffModal.title = entry.action + (entry.entityName ? ' — ' + entry.entityName : '')
      this.diffModal.show = true
    },

    actionVariant (action) {
      return ACTION_VARIANTS[action] || 'secondary'
    },

    isCreateAction (action) {
      return CREATE_ACTIONS.indexOf(action) !== -1
    },

    formatDate (dateStr) {
      if (!dateStr) return '—'
      var d = new Date(dateStr)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    },

    formatDateFull (dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      })
    }
  }
}
</script>
